import { DNA_ATTRIBUTE_CONFIG_MAP, DNAAttribute } from "pages/cabal-id/dna/dnaConfig";

import { Box, Typography, useTheme } from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import { Slider } from "@mui/material";
import Model from "../base/Model/ui";
import { Col } from "../base/Flex/ui";
import { Row } from "../base/Flex/ui";
import BasicButton from "../Buttons/BasicButton";
import CheckBox from "../base/CheckBox/ui";

const traitDescription: Record<DNAAttribute, string> = {
  earlyScore: "Spots winners before the crowd",
  accuracyScore: "Consistently calls tokens that go up",
  socialScore: "Commands strong social engagement",
  pumpScore: "Drives high volume and fast action in the market",
  insightScore: "Offers deep project and narrative analysis",
};

const DEFAULT_SELECTED_TRAITS: [DNAAttribute, number[]][] = [
  ["accuracyScore", [0, 5]],
  ["pumpScore", [0, 5]],
  ["socialScore", [0, 5]],
  ["earlyScore", [0, 5]],
  ["insightScore", [0, 5]],
];

export const FilterByTraitModal = ({
  open,
  onClose,
  selectedTraits,
  setSelectedTraits,
}: {
  open: boolean;
  onClose: () => void;
  selectedTraits: Map<DNAAttribute, number[]>;
  setSelectedTraits: (traits: Map<DNAAttribute, number[]>) => void;
}) => {
  const [tempSelectedTraits, setTempSelectedTraits] = useState<Map<DNAAttribute, number[]>>(
    new Map(selectedTraits),
  );

  useEffect(() => {
    setTempSelectedTraits(new Map(selectedTraits));
  }, [selectedTraits]);

  const handleApplySelectedTraits = () => {
    setSelectedTraits(new Map(tempSelectedTraits));
    localStorage.setItem(
      "selectedTraits",
      JSON.stringify(Array.from(tempSelectedTraits.entries())),
    );
    onClose();
  };

  const handleClose = () => {
    setTempSelectedTraits(new Map(DEFAULT_SELECTED_TRAITS));
    onClose();
  };

  return (
    <Model.Wrapper open={open} onClose={handleClose}>
      <Model.Container size="sm">
        <Model.Header.Wrapper>
          <Model.Header.Title>
            <Col>
              <Typography variant="mobile-body-m-heavy">FILTER BY TRAITS</Typography>
              <Typography variant="mobile-body-s" color="text.secondary">
                Select up to 3
              </Typography>
            </Col>
          </Model.Header.Title>
          <Model.Header.CloseButton close={handleClose} />
        </Model.Header.Wrapper>
        <Model.Content>
          <Row type="cc" sx={{ padding: "32px 0" }} fullWidth>
            <Col
              sx={{
                gap: "20px",
                marginY: "0px",
                maxHeight: "60vh",
                overflowY: "scroll",
                overflowX: "hidden",
              }}
              fullWidth
              fullHeight>
              {Object.keys(DNA_ATTRIBUTE_CONFIG_MAP).map((attributeName) => {
                return (
                  <TraitItem
                    key={attributeName}
                    attributeName={attributeName as DNAAttribute}
                    tempSelectedTraits={tempSelectedTraits}
                    setTempSelectedTraits={setTempSelectedTraits}
                  />
                );
              })}
            </Col>
          </Row>
          <Row fullWidth>
            <BasicButton variant="white" sx={{ width: "100%" }} onClick={handleApplySelectedTraits}>
              <Typography variant="mobile-action-s-heavy">Apply</Typography>
            </BasicButton>
          </Row>
        </Model.Content>
      </Model.Container>
    </Model.Wrapper>
  );
};

const TraitItem = ({
  attributeName,
  tempSelectedTraits,
  setTempSelectedTraits,
}: {
  attributeName: DNAAttribute;
  tempSelectedTraits: Map<DNAAttribute, number[]>;
  setTempSelectedTraits: (traits: Map<DNAAttribute, number[]>) => void;
}) => {
  const [isDisabled, setIsDisabled] = useState(false);
  const [isSelected, setIsSelected] = useState(
    tempSelectedTraits.get(attributeName as DNAAttribute)?.[0] !== 0 ||
      tempSelectedTraits.get(attributeName as DNAAttribute)?.[1] !== 5,
  );

  const isDefaultValues = useMemo(() => {
    return (
      tempSelectedTraits.get(attributeName as DNAAttribute)?.[0] === 0 &&
      tempSelectedTraits.get(attributeName as DNAAttribute)?.[1] === 5
    );
  }, [tempSelectedTraits, attributeName]);

  const theme = useTheme();

  const dnaAttributeConfig = DNA_ATTRIBUTE_CONFIG_MAP[attributeName as DNAAttribute]!;

  useEffect(() => {
    if (isDefaultValues) {
      setIsSelected(false);
    } else {
      setIsSelected(true);
    }

    if (
      [...tempSelectedTraits.values()]?.filter((value) => value[0] !== 0 || value[1] !== 5)
        .length >= 3
    ) {
      setIsDisabled(true);
    } else {
      setIsDisabled(false);
    }
  }, [tempSelectedTraits]);

  // if (attributeName === "insightScore") {
  //   return null;
  // }

  const handleSelectTrait = () => {
    if (isSelected) {
      setIsSelected(false);
      tempSelectedTraits.set(attributeName as DNAAttribute, [0, 5]);
    } else {
      setIsSelected(true);
      tempSelectedTraits.set(attributeName as DNAAttribute, [4, 5]);
    }
    setTempSelectedTraits(new Map(tempSelectedTraits));
  };

  return (
    <Col
      type="bs"
      key={attributeName}
      sx={{ gap: "4px", opacity: isDisabled && !isSelected ? 0.5 : 1 }}
      fullWidth>
      <Row type="bc" gap="16px" paddingX="4px" fullWidth justifyContent="space-between">
        <Row type="sc" gap="16px">
          <CheckBox isSelected={isSelected} handleSelect={handleSelectTrait} />
          <Typography variant="mobile-body-m-heavy">{dnaAttributeConfig.name}</Typography>
        </Row>
        <Typography variant="mobile-body-xs" color="text.secondary" textAlign="right">
          {traitDescription[attributeName as DNAAttribute]}
        </Typography>
      </Row>
      <Box
        sx={{
          paddingX: "14px",
          width: "100%",
        }}>
        <TraitSlider
          attributeName={attributeName}
          tempSelectedTraits={tempSelectedTraits}
          setTempSelectedTraits={setTempSelectedTraits}
          isDisabled={isDisabled}
          isSelected={isSelected}
        />
      </Box>
    </Col>
  );
};

const TraitSlider = ({
  attributeName,
  tempSelectedTraits,
  setTempSelectedTraits,
  isDisabled,
  isSelected,
}: {
  attributeName: DNAAttribute;
  tempSelectedTraits: Map<DNAAttribute, number[]>;
  setTempSelectedTraits: (traits: Map<DNAAttribute, number[]>) => void;
  isDisabled: boolean;
  isSelected: boolean;
}) => {
  const minDistance = 1;
  const theme = useTheme();

  const handleChange = (event: Event, newValue: number[], activeThumb: number) => {
    const values = tempSelectedTraits.get(attributeName as DNAAttribute)!;
    if (activeThumb === 0) {
      tempSelectedTraits.set(attributeName as DNAAttribute, [
        Math.min(newValue[0], values[1] - minDistance),
        values[1],
      ]);
      setTempSelectedTraits(new Map(tempSelectedTraits));
    } else {
      tempSelectedTraits.set(attributeName as DNAAttribute, [
        values[0],
        Math.max(newValue[1], values[0] + minDistance),
      ]);
      setTempSelectedTraits(new Map(tempSelectedTraits));
    }
  };

  return (
    <Slider
      disabled={isDisabled && !isSelected}
      getAriaLabel={() => "Minimum distance"}
      value={tempSelectedTraits.get(attributeName as DNAAttribute)!}
      onChange={(event, newValue: number | number[], activeThumb: number) =>
        handleChange(event, newValue as number[], activeThumb)
      }
      getAriaValueText={(value) => `${value}°C`}
      disableSwap
      max={5}
      min={0}
      marks={[
        {
          value: 0,
          label: <Typography variant="body-sm">Low</Typography>,
        },
        {
          value: 5,
          label: <Typography variant="body-sm">High</Typography>,
        },
      ]}
    />
  );
};
